/**
 * Raffinato Theme JavaScript
 * Luxury jewelry e-commerce functionality
 */

class RaffinatoTheme {
  constructor() {
    this.init();
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  init() {
    this.injectCartButtonStyles();
    this.initMobileMenu();
    this.initStickyHeader();
    this.initProductGallery();
    this.initAccordions();
    this.initQuantitySelectors();
    this.initCartDrawer();
    this.initSearchModal();
    this.initQuickView();
    this.initFilters();
    this.initColorSwatches();
    this.initLanguageToggle();
    this.initGoldPriceTicker();
  }

  // Inject CSS for Add-to-Cart button states (loading / success / error)
  injectCartButtonStyles() {
    if (document.getElementById('raffinato-cart-btn-styles')) return;
    const style = document.createElement('style');
    style.id = 'raffinato-cart-btn-styles';
    style.textContent = `
      /* Smooth transitions for all ATC button state changes */
      form[action*="/cart/add"] [type="submit"],
      form[action*="/cart/add"] button:not([type="button"]) {
        transition: background-color 0.3s ease, color 0.3s ease, opacity 0.3s ease;
      }

      /* Loading state — subtle pulse */
      .pd-form__submit--loading {
        opacity: 0.7;
        pointer-events: none;
        cursor: wait;
      }

      /* Success state — gold background */
      .pd-form__submit--success {
        background-color: #B8956A !important;
        color: #fff !important;
        border-color: #B8956A !important;
      }

      /* Checkmark icon inside success state */
      .pd-form__submit-check {
        display: inline-block;
        font-size: 1.1em;
        line-height: 1;
        vertical-align: middle;
      }

      /* Error state — brief red flash */
      .pd-form__submit--error {
        background-color: #c0392b !important;
        color: #fff !important;
        border-color: #c0392b !important;
      }
    `;
    document.head.appendChild(style);
  }

  // Mobile Menu
  initMobileMenu() {
    const toggleBtn = document.querySelector('.mobile-menu-toggle');
    const mobileMenu = document.querySelector('.mobile-menu');

    if (!toggleBtn || !mobileMenu) return;

    toggleBtn.addEventListener('click', () => {
      mobileMenu.classList.toggle('mobile-menu--active');
      document.body.classList.toggle('menu-open');
    });

    // Close menu when a nav link is clicked
    const menuLinks = mobileMenu.querySelectorAll('.mobile-nav__link');
    menuLinks.forEach(link => {
      link.addEventListener('click', () => {
        mobileMenu.classList.remove('mobile-menu--active');
        document.body.classList.remove('menu-open');
      });
    });

    // Close menu on outside click
    document.addEventListener('click', (e) => {
      if (!toggleBtn.contains(e.target) && !mobileMenu.contains(e.target)) {
        mobileMenu.classList.remove('mobile-menu--active');
        document.body.classList.remove('menu-open');
      }
    });
  }

  // Sticky Header
  initStickyHeader() {
    const header = document.querySelector('.header--sticky');
    if (!header) return;

    let lastScroll = 0;
    const headerHeight = header.offsetHeight;
    let scrollTicking = false;

    window.addEventListener('scroll', function() {
      if (!scrollTicking) {
        requestAnimationFrame(() => {
          const currentScroll = window.pageYOffset;
          if (currentScroll > headerHeight) {
            if (currentScroll > lastScroll) {
              header.style.transform = 'translateY(-100%)';
            } else {
              header.style.transform = 'translateY(0)';
            }
          }
          lastScroll = currentScroll;
          scrollTicking = false;
        });
        scrollTicking = true;
      }
    }, { passive: true });
  }

  // Product Gallery
  initProductGallery() {
    const thumbs = document.querySelectorAll('.product-gallery__thumb');
    const slides = document.querySelectorAll('.product-gallery__slide');

    if (!thumbs.length || !slides.length) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const mediaId = thumb.dataset.mediaId;

        // Update active states
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');

        slides.forEach(slide => {
          if (slide.dataset.mediaId === mediaId) {
            slide.classList.add('active');
          } else {
            slide.classList.remove('active');
          }
        });
      });
    });
  }

  // Accordions
  initAccordions() {
    const triggers = document.querySelectorAll('.accordion__trigger');

    const toggleAccordion = (trigger) => {
      const contentId = trigger.dataset.accordion;
      const content = document.getElementById(contentId);

      if (!content) return;

      trigger.classList.toggle('active');
      content.classList.toggle('active');

      // Update aria
      const isOpen = trigger.classList.contains('active');
      trigger.setAttribute('aria-expanded', isOpen);

      // Close other accordions
      triggers.forEach(otherTrigger => {
        if (otherTrigger !== trigger) {
          otherTrigger.classList.remove('active');
          otherTrigger.setAttribute('aria-expanded', 'false');
          const otherContent = document.getElementById(otherTrigger.dataset.accordion);
          if (otherContent) otherContent.classList.remove('active');
        }
      });
    };

    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => toggleAccordion(trigger));
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleAccordion(trigger);
        }
      });
    });
  }

  // Quantity Selectors
  initQuantitySelectors() {
    const quantityButtons = document.querySelectorAll('.quantity__button');

    quantityButtons.forEach(button => {
      button.addEventListener('click', () => {
        const input = button.parentElement.querySelector('.quantity__input');
        const currentValue = parseInt(input.value) || 1;
        const action = button.dataset.action;

        if (action === 'plus') {
          input.value = currentValue + 1;
        } else if (action === 'minus' && currentValue > 1) {
          input.value = currentValue - 1;
        }

        // Trigger change event
        input.dispatchEvent(new Event('change'));
      });
    });
  }

  // Cart Drawer
  initCartDrawer() {
    const cartTriggers = document.querySelectorAll('[data-cart-trigger]');
    const cartDrawer = document.querySelector('.cart-drawer');
    const cartClose = document.querySelector('.cart-drawer__close');

    if (!cartDrawer) return;

    cartTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openCartDrawer();
      });
    });

    if (cartClose) {
      cartClose.addEventListener('click', () => {
        this.closeCartDrawer();
      });
    }

    // Add to cart form submission
    const addToCartForms = document.querySelectorAll('form[action*="/cart/add"]');
    addToCartForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart(form);
      });
    });
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('.cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.add('cart-drawer--open');
      document.body.classList.add('drawer-open');
    }
  }

  closeCartDrawer() {
    const cartDrawer = document.querySelector('.cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.remove('cart-drawer--open');
      document.body.classList.remove('drawer-open');
    }
  }

  async addToCart(form) {
    const formData = new FormData(form);
    const submitBtn = form.querySelector('[type="submit"], button:not([type="button"])');

    // Read text states from data attributes, with English fallbacks
    const addText    = submitBtn ? (submitBtn.getAttribute('data-add-text')    || submitBtn.textContent.trim()) : 'Add to Cart';
    const addingText = submitBtn ? (submitBtn.getAttribute('data-adding-text') || 'Adding...') : 'Adding...';
    const addedText  = submitBtn ? (submitBtn.getAttribute('data-added-text')  || 'Added') : 'Added';

    // --- Loading state ---
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = addingText;
      submitBtn.classList.add('pd-form__submit--loading');
    }

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const item = await response.json();

        // --- Success state ---
        if (submitBtn) {
          submitBtn.classList.remove('pd-form__submit--loading');
          submitBtn.classList.add('pd-form__submit--success');
          submitBtn.innerHTML = '<span class="pd-form__submit-check">&#10003;</span> ' + addedText;
        }

        this.updateCartDrawer();
        this.openCartDrawer();

        // Revert after 2 seconds
        setTimeout(() => {
          if (submitBtn) {
            submitBtn.classList.remove('pd-form__submit--success');
            submitBtn.textContent = addText;
            submitBtn.disabled = false;
          }
        }, 2000);
      } else {
        // Non-OK HTTP response (e.g. 422 variant unavailable)
        throw new Error('Server returned ' + response.status);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);

      // --- Error state ---
      if (submitBtn) {
        submitBtn.classList.remove('pd-form__submit--loading');
        submitBtn.classList.add('pd-form__submit--error');
        submitBtn.textContent = addText;
      }

      // Revert error state after 1.5 seconds
      setTimeout(() => {
        if (submitBtn) {
          submitBtn.classList.remove('pd-form__submit--error');
          submitBtn.disabled = false;
        }
      }, 1500);
    }
  }

  async updateCartDrawer() {
    try {
      // Fetch cart JSON for badge count
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartResponse.json();

      // Update cart count badges in the header
      const cartCounts = document.querySelectorAll('.header__cart-count');
      cartCounts.forEach(count => {
        count.textContent = cart.item_count;
        count.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });

      // Fetch fresh cart drawer HTML from the current page
      const htmlResponse = await fetch(window.location.pathname, {
        headers: { 'Accept': 'text/html' }
      });
      const html = await htmlResponse.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const freshDrawer = doc.querySelector('#cartDrawer');
      const currentDrawer = document.querySelector('#cartDrawer');

      if (freshDrawer && currentDrawer) {
        // Preserve open state before replacing content
        const wasOpen = currentDrawer.classList.contains('cart-drawer--open');
        currentDrawer.innerHTML = freshDrawer.innerHTML;
        if (wasOpen) {
          currentDrawer.classList.add('cart-drawer--open');
          document.body.classList.add('drawer-open');
        }
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  // Search Modal
  initSearchModal() {
    const searchTriggers = document.querySelectorAll('[data-search-trigger]');
    const searchModal = document.querySelector('.search-modal');
    const searchClose = document.querySelector('.search-modal__close');

    if (!searchModal) return;

    searchTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        searchModal.classList.add('search-modal--open');
        document.body.classList.add('modal-open');

        // Focus search input
        const searchInput = searchModal.querySelector('.search-modal__input');
        if (searchInput) {
          setTimeout(() => searchInput.focus(), 100);
        }
      });
    });

    if (searchClose) {
      searchClose.addEventListener('click', () => {
        searchModal.classList.remove('search-modal--open');
        document.body.classList.remove('modal-open');
      });
    }

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchModal.classList.contains('search-modal--open')) {
        searchModal.classList.remove('search-modal--open');
        document.body.classList.remove('modal-open');
      }
    });
  }

  // Quick View
  initQuickView() {
    const quickViewButtons = document.querySelectorAll('.product-card__quick-view');

    quickViewButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        const handle = button.dataset.productHandle;

        // Get image from product card as fallback
        const productCard = button.closest('.product-card');
        const cardImage = productCard ? productCard.querySelector('.product-card__image--primary') : null;
        const fallbackImageUrl = cardImage ? cardImage.src : null;

        await this.loadQuickView(handle, fallbackImageUrl);
      });
    });
  }

  async loadQuickView(handle, fallbackImageUrl) {
    try {
      const response = await fetch(window.Shopify.routes.root + `products/${handle}.js`);
      const product = await response.json();


      // Render quick view modal with product data
      this.renderQuickViewModal(product, fallbackImageUrl);
    } catch (error) {
      console.error('Error loading quick view:', error);
    }
  }

  renderQuickViewModal(product, fallbackImageUrl) {
    const modal = document.querySelector('.quick-view-modal');
    if (!modal) return;

    const content = document.getElementById('quickViewContent');
    if (!content) return;

    // Build the quick view HTML
    const firstVariant = product.variants[0];
    const isAvailable = product.available;

    // Get image URL - Shopify's .js endpoint returns featured_image as string URL
    let imageUrl = product.featured_image || '';

    // Fallback to first image in images array
    if (!imageUrl && product.images && product.images.length > 0) {
      imageUrl = product.images[0];
    }

    // Fallback to variant image
    if (!imageUrl && firstVariant && firstVariant.featured_image) {
      imageUrl = firstVariant.featured_image;
    }

    // Use fallback from product card if still no image
    if (!imageUrl && fallbackImageUrl) {
      imageUrl = fallbackImageUrl;
    }


    // Build price or sold out display
    const priceDisplay = isAvailable ? `
      <div class="quick-view__price">
        <span>${(firstVariant.price / 100).toFixed(2)}</span>
        <span class="sar-symbol">&#xE900;</span>
      </div>
    ` : `<div class="quick-view__sold-out">Sold Out</div>`;

    const escapedTitle = this.escapeHtml(product.title);
    const escapedDesc = product.description
      ? this.escapeHtml(product.description.replace(/<[^>]*>/g, ''))
      : '';

    const html = `
      <div class="quick-view-grid">
        <div class="quick-view__images">
          ${imageUrl ? `<img src="${imageUrl}" alt="${escapedTitle}" class="quick-view__main-image">` : '<div class="quick-view__placeholder">No Image</div>'}
        </div>
        <div class="quick-view__details">
          <h2 class="quick-view__title">${escapedTitle}</h2>
          ${priceDisplay}
          ${escapedDesc ? `<div class="quick-view__description">${escapedDesc}</div>` : ''}
          <a href="${window.Shopify.routes.root}products/${product.handle}" class="quick-view__button">View Full Details</a>
        </div>
      </div>
    `;

    content.innerHTML = html;

    // Add error handler for image (replaces inline onerror)
    const img = content.querySelector('.quick-view__main-image');
    if (img) {
      img.addEventListener('error', function() {
        this.parentElement.innerHTML = '<div class="quick-view__placeholder">Image not available</div>';
      });
    }

    modal.classList.add('quick-view-modal--open');
    document.body.classList.add('modal-open');

    // Add close handler
    const closeBtn = modal.querySelector('.quick-view-modal__close');
    const overlay = modal.querySelector('.quick-view-modal__overlay');

    const closeModal = () => {
      modal.classList.remove('quick-view-modal--open');
      document.body.classList.remove('modal-open');
    };

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (overlay) overlay.addEventListener('click', closeModal);
  }

  // Filters
  initFilters() {
    const filterDropdowns = document.querySelectorAll('.filter-dropdown');

    filterDropdowns.forEach(dropdown => {
      const trigger = dropdown.querySelector('.filter-dropdown__trigger');

      if (trigger) {
        trigger.addEventListener('click', (e) => {
          e.stopPropagation();

          // Close other dropdowns
          filterDropdowns.forEach(other => {
            if (other !== dropdown) {
              other.classList.remove('filter-dropdown--active');
            }
          });

          dropdown.classList.toggle('filter-dropdown--active');
        });
      }
    });

    // Close dropdowns on outside click
    document.addEventListener('click', () => {
      filterDropdowns.forEach(dropdown => {
        dropdown.classList.remove('filter-dropdown--active');
      });
    });

    // Handle filter checkbox changes
    const filterCheckboxes = document.querySelectorAll('.filter-option input[type="checkbox"]');
    filterCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.updateFilters();
      });
    });

    // Handle sort change
    const sortSelect = document.getElementById('sortBy');
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        this.updateSort(sortSelect.value);
      });
    }

    // Clear all filters
    const clearButton = document.querySelector('.clear-all-filters');
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        this.clearAllFilters();
      });
    }
  }

  updateFilters() {
    // Collect all active filters
    const activeFilters = [];
    const checkboxes = document.querySelectorAll('.filter-option input[type="checkbox"]:checked');

    checkboxes.forEach(checkbox => {
      activeFilters.push({
        type: checkbox.dataset.filterType,
        value: checkbox.value
      });
    });

    // Update UI
    this.renderActiveFilters(activeFilters);

    // Update products (would make AJAX request to filter products)
    this.filterProducts(activeFilters);
  }

  renderActiveFilters(filters) {
    const container = document.getElementById('activeFilters');
    const list = container.querySelector('.active-filters__list');

    if (!filters.length) {
      container.style.display = 'none';
      return;
    }

    container.style.display = 'flex';
    list.innerHTML = filters.map(filter => `
      <span class="active-filter-tag" data-filter-type="${filter.type}" data-filter-value="${filter.value}">
        ${filter.value}
        <span class="active-filter-tag__remove">×</span>
      </span>
    `).join('');

    // Add remove handlers
    list.querySelectorAll('.active-filter-tag__remove').forEach(button => {
      button.addEventListener('click', (e) => {
        const tag = e.target.closest('.active-filter-tag');
        const type = tag.dataset.filterType;
        const value = tag.dataset.filterValue;

        // Uncheck corresponding checkbox
        const checkbox = document.querySelector(`input[data-filter-type="${type}"][value="${value}"]`);
        if (checkbox) {
          checkbox.checked = false;
          this.updateFilters();
        }
      });
    });
  }

  filterProducts(filters) {
    if (!filters.length) {
      document.querySelectorAll('.product-card').forEach(card => {
        card.style.display = '';
      });
      return;
    }
    document.querySelectorAll('.product-card').forEach(card => {
      const cardCategory = card.dataset.productCategory || '';
      const cardColor = card.dataset.productColor || '';
      const cardStone = card.dataset.productStone || '';
      const cardPrice = parseInt(card.dataset.productPrice || '0', 10);
      let show = true;
      filters.forEach(filter => {
        if (filter.type === 'color' && cardColor && !cardColor.includes(filter.value)) show = false;
        if (filter.type === 'stone' && cardStone && !cardStone.includes(filter.value)) show = false;
      });
      card.style.display = show ? '' : 'none';
    });
  }

  updateSort(sortBy) {
    // Update URL and reload products with new sort order
    const url = new URL(window.location);
    url.searchParams.set('sort_by', sortBy);
    if (url.origin === window.location.origin) {
      window.location = url.toString();
    }
  }

  clearAllFilters() {
    const checkboxes = document.querySelectorAll('.filter-option input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });
    this.updateFilters();
  }

  // Color Swatches for Product Cards
  initColorSwatches() {
    const productCards = document.querySelectorAll('.product-card');

    productCards.forEach(card => {
      const swatches = card.querySelectorAll('.color-swatch');
      const primaryImage = card.querySelector('.product-card__image--primary');

      if (!swatches.length || !primaryImage) return;

      // Store original image
      const originalImageUrl = primaryImage.src;

      swatches.forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Update active state
          swatches.forEach(s => s.classList.remove('color-swatch--active'));
          swatch.classList.add('color-swatch--active');

          // Change image if available
          const imageUrl = swatch.dataset.imageUrl;
          if (imageUrl && primaryImage) {
            primaryImage.src = imageUrl;
            primaryImage.srcset = `${imageUrl} 600w`;
          }
        });

        // Preview on hover (optional)
        swatch.addEventListener('mouseenter', () => {
          const imageUrl = swatch.dataset.imageUrl;
          if (imageUrl && primaryImage) {
            primaryImage.src = imageUrl;
            primaryImage.srcset = `${imageUrl} 600w`;
          }
        });

        swatch.addEventListener('mouseleave', () => {
          // Restore to active swatch image
          const activeSwatch = card.querySelector('.color-swatch--active');
          if (activeSwatch && activeSwatch.dataset.imageUrl && primaryImage) {
            primaryImage.src = activeSwatch.dataset.imageUrl;
            primaryImage.srcset = `${activeSwatch.dataset.imageUrl} 600w`;
          }
        });
      });
    });
  }

  // Language Toggle — form-based per Shopify docs
  initLanguageToggle() {
    document.querySelectorAll('localization-form').forEach(locForm => {
      const btn  = locForm.querySelector('.language-toggle__btn');
      const list = locForm.querySelector('.language-toggle__dropdown');
      const input = locForm.querySelector('input[name="language_code"]');
      if (!btn || !list) return;

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const isOpen = !list.hasAttribute('hidden');
        if (isOpen) {
          list.setAttribute('hidden', '');
          btn.setAttribute('aria-expanded', 'false');
        } else {
          list.removeAttribute('hidden');
          btn.setAttribute('aria-expanded', 'true');
        }
      });

      // Language option clicks submit the localization form
      locForm.querySelectorAll('.language-toggle__option').forEach(option => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          if (input) {
            input.value = option.dataset.value;
            const form = locForm.querySelector('form');
            if (form) form.submit();
          }
        });
      });

      // Close on click outside
      document.addEventListener('click', (e) => {
        if (!locForm.contains(e.target)) {
          list.setAttribute('hidden', '');
          btn.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  // ── Gold Price Ticker ────────────────────────────────────
  initGoldPriceTicker() {
    const ticker = document.getElementById('goldPriceTicker');
    if (!ticker) return;

    const API_URL = 'https://18-199-153-159.sslip.io/api/gold-price';
    const CACHE_KEY = 'raffinato_gold_price';
    const CACHE_TTL = 30 * 60 * 1000; // 30 min client-side cache

    const priceEl = document.getElementById('goldTickerPrice');
    const karatEl = document.getElementById('goldTickerKarat');
    const productKarat = ticker.dataset.productKarat || '';

    // Determine karat from product data or tag
    let karat = productKarat;
    if (!karat) {
      const karatPill = document.querySelector('.pd-spec-pill');
      if (karatPill) {
        const match = karatPill.textContent.match(/(\d+)\s*K/i);
        if (match) karat = match[1];
      }
    }
    if (!karat) karat = '21'; // default for Saudi market

    const updateTicker = (data) => {
      const prices = data.prices_by_karat || {};
      const price = prices[karat] || data.price_per_gram_24k;
      if (!price) return;

      // Format number with locale-aware separators
      const lang = document.documentElement.lang || 'en';
      const formatted = new Intl.NumberFormat(lang === 'ar' ? 'ar-SA' : 'en-SA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(price);

      priceEl.textContent = formatted;
      if (karatEl) karatEl.textContent = karat + 'K';
      ticker.style.display = '';
    };

    // Check sessionStorage cache first
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL) {
          updateTicker(data);
          return; // fresh cache, skip fetch
        }
      }
    } catch (e) { /* ignore storage errors */ }

    // Fetch live price
    fetch(API_URL, { mode: 'cors' })
      .then(r => {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then(data => {
        updateTicker(data);
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
        } catch (e) { /* quota exceeded, ignore */ }
      })
      .catch(() => {
        // Silently fail — ticker stays hidden if no price available
      });
  }
}

// Initialize theme
document.addEventListener('DOMContentLoaded', () => {
  new RaffinatoTheme();
});

// Shopify theme editor support
document.addEventListener('shopify:section:load', () => {
  new RaffinatoTheme();
});

